import { motion } from "framer-motion";
import { ArrowUpRight, Bus, Building2, FileImage, Palmtree, Plane, Train } from "lucide-react";
import { IMAGES } from "@/config";

const EASE = [0.16, 1, 0.3, 1];

const PhotoCard = ({ testid, name, note, tag, img, span, onPick, delay }) => (
  <motion.button
    data-testid={testid}
    onClick={() => onPick(name)}
    className={`group relative h-80 overflow-hidden border border-ink/15 text-left md:h-[24rem] ${span}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    <img
      src={img}
      alt={name}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-ink/10 transition-opacity duration-500" aria-hidden="true" />
    <div className="relative flex h-full flex-col justify-between p-6 sm:p-8">
      <div className="flex items-start justify-between">
        <span className="rounded-full border border-ivory/40 bg-ink/30 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ivory backdrop-blur-sm">
          {tag}
        </span>
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ivory text-ink transition-all duration-300 group-hover:bg-cyan group-hover:text-ink">
          <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
        </span>
      </div>
      <div>
        <h3 className="font-serif text-3xl font-medium text-ivory sm:text-4xl">{name}</h3>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-ivory/80">{note}</p>
        <p className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan">
          <FileImage className="h-3.5 w-3.5" /> Send your quote + screenshot
        </p>
      </div>
    </div>
  </motion.button>
);

const TrainCard = ({ onPick, delay }) => (
  <motion.div
    data-testid="trains-tab"
    className="group relative h-80 overflow-hidden border border-ocean bg-ocean md:h-[24rem] md:col-span-7"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    <img
      src={IMAGES.train}
      alt="Indian train journey through the mountains"
      loading="lazy"
      className="absolute inset-y-0 right-0 hidden h-full w-1/2 object-cover opacity-70 md:block"
    />
    <div className="absolute inset-y-0 right-0 hidden w-1/2 bg-gradient-to-r from-ocean via-ocean/60 to-transparent md:block" aria-hidden="true" />
    <div className="relative flex h-full flex-col justify-between p-6 sm:p-8 md:w-2/3">
      <div className="flex items-start justify-between">
        <span className="rounded-full bg-cyan px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink">
          Zero fees above ₹2,000
        </span>
        <Train className="h-6 w-6 text-ivory/80" />
      </div>
      <div>
        <h3 className="font-serif text-3xl font-medium text-ivory sm:text-4xl">Trains</h3>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-ivory/80">
          No booking fee on any train ticket above ₹2,000. Availability and confirmations
          checked by a human, not a bot.
        </p>
        <button
          data-testid="trains-pick-button"
          onClick={() => onPick("Trains")}
          className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan transition-colors hover:text-ivory"
        >
          Request a train price <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  </motion.div>
);

const BusStrip = ({ onPick, delay }) => (
  <motion.div
    data-testid="buses-tab"
    className="group relative flex flex-col gap-6 border border-ink/15 bg-cyan-soft p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8 md:col-span-12"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    <div className="flex items-start gap-5">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-ink text-ivory">
        <Bus className="h-5 w-5" />
      </span>
      <div>
        <h3 className="font-serif text-2xl font-medium text-ink sm:text-3xl">Buses</h3>
        <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-ink2">
          Volvo, sleeper, AC — send your quote, the screenshot and the price you're getting.
          We come back with a better one.
        </p>
      </div>
    </div>
    <button
      data-testid="buses-pick-button"
      onClick={() => onPick("Buses")}
      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-ocean active:scale-[0.98]"
    >
      Send your bus quote <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
    </button>
  </motion.div>
);

const Categories = ({ onPick }) => (
  <section id="categories" className="py-20 lg:py-32" data-testid="category-tabs">
    <div className="container-edit">
      <div className="mb-14 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <motion.p
            className="eyebrow mb-5 flex items-center gap-4"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <span className="inline-block h-px w-10 bg-cyan" /> What we price
          </motion.p>
          <motion.h2
            className="font-serif text-3xl font-medium leading-tight tracking-tight text-ink sm:text-4xl"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
          >
            Five categories. One obsession: <em className="italic text-ocean">a better number.</em>
          </motion.h2>
        </div>
        <motion.p
          className="text-base leading-relaxed text-ink2 lg:col-span-4"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
        >
          Wherever you're headed, send the quote you're getting. If we can't beat it, we'll
          say so straight.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
        <PhotoCard
          testid="flights-tab"
          name="Flights"
          note="Domestic or international — show us the fare you're getting and we hunt a better one."
          tag="Screenshot + price"
          img={IMAGES.flight}
          span="md:col-span-7"
          onPick={onPick}
          delay={0}
        />
        <PhotoCard
          testid="hotels-tab"
          name="Hotels"
          note="Any hotel, any city, same room. Better rate, no loyalty points sacrificed."
          tag="Screenshot + price"
          img={IMAGES.hotel}
          span="md:col-span-5"
          onPick={onPick}
          delay={0.1}
        />
        <PhotoCard
          testid="packages-tab"
          name="Packages"
          note="Tell us the itinerary and the number you were handed — we rework it line by line."
          tag="Custom itinerary"
          img={IMAGES.kerala}
          span="md:col-span-5"
          onPick={onPick}
          delay={0}
        />
        <TrainCard onPick={onPick} delay={0.1} />
        <BusStrip onPick={onPick} delay={0.15} />
      </div>
    </div>
  </section>
);

export default Categories;
